import "../../../css/SettingsTab.css";
import React, { useState, useEffect } from "react";
import { Input, Switch, Select, Button, Spin, message } from "antd";
import {
  updateSubscription,
  rotateWebhookSecret,
  getOAuthAuthorizeUrl,
} from "../../../services/integrations/klaviyoSubscriptionService";
import DisconnectModal from "./DisconnectModal";
import WebhookSecretModal from "./WebhookSecretModal";
import WebhookSetupCard from "./WebhookSetupCard";

const SCHEDULE_OPTIONS = [
  { value: "daily_03_utc", label: "Daily at 03:00 UTC" },
  { value: "hourly", label: "Hourly" },
  { value: "every_6_hours", label: "Every 6 hours" },
  { value: "manual_only", label: "Manual only" },
];

export default function SettingsTab({ subscription, summary, onRefresh, onDisconnect }) {
  const [name, setName] = useState(subscription?.name || "");
  const [enabled, setEnabled] = useState(subscription?.enabled ?? true);
  const [schedule, setSchedule] = useState(subscription?.sync_schedule || "daily_03_utc");
  const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
  const [webhookSecretModalOpen, setWebhookSecretModalOpen] = useState(false);
  const [webhookSecret, setWebhookSecret] = useState(null);
  const [rotatingSecret, setRotatingSecret] = useState(false);

  useEffect(() => {
    setName(subscription?.name || "");
    setEnabled(subscription?.enabled ?? true);
    setSchedule(subscription?.sync_schedule || "daily_03_utc");
  }, [subscription]);

  const handleWebhookSecret = async () => {
    setRotatingSecret(true);
    try {
      const res = await rotateWebhookSecret();
      setWebhookSecret(res?.webhook_secret);
      setWebhookSecretModalOpen(true);
    } catch {
      message.error("Failed to generate webhook secret");
    }
    setRotatingSecret(false);
  };

  const handleWebhookSecretDismiss = () => {
    setWebhookSecretModalOpen(false);
    setWebhookSecret(null);
    onRefresh();
  };

  const formatWebhookDate = () => {
    if (!subscription?.webhook_secret_rotated_at) return null;
    const d = new Date(subscription.webhook_secret_rotated_at);
    return "Last rotated: " + d.toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric",
    });
  };

  const handleNameBlur = async () => {
    if (name.trim() === (subscription?.name || "")) return;
    try {
      await updateSubscription({ name: name.trim() });
      message.success("Subscription name updated");
      onRefresh();
    } catch {
      message.error("Failed to update subscription name");
      setName(subscription?.name || "");
    }
  };

  const handleEnabledChange = async (val) => {
    const prev = enabled;
    setEnabled(val);
    try {
      await updateSubscription({ enabled: val });
      message.success(val ? "Integration enabled" : "Integration disabled");
      onRefresh();
    } catch {
      setEnabled(prev);
      message.error("Failed to update status");
    }
  };

  const handleScheduleChange = async (val) => {
    const prev = schedule;
    setSchedule(val);
    try {
      await updateSubscription({ sync_schedule: val });
      message.success("Sync schedule updated");
      onRefresh();
    } catch {
      setSchedule(prev);
      message.error("Failed to update sync schedule");
    }
  };

  return (
    <div className="settings-tab-container">
      {/* Connection */}
      <div className="settings-section">
        <div className="settings-section-title">Connection</div>

        <div className="settings-row">
          <div className="settings-field">
            <div className="settings-label">Subscription Name</div>
          </div>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={handleNameBlur}
            onPressEnter={handleNameBlur}
            style={{ width: 300 }}
          />
        </div>

        {subscription?.auth_mode === "OAUTH_REAUTH_REQUIRED" ? (
          <div className="settings-row">
            <div className="settings-field">
              <div className="settings-label">Authentication</div>
              <div className="settings-hint" style={{ color: "#d4380d" }}>
                OAuth connection expired or was revoked. Please reconnect.
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 13, color: "#d4380d", fontWeight: 500 }}>
                Reconnection required
              </span>
              <Button type="primary" size="small" danger onClick={() => {
                getOAuthAuthorizeUrl().then(url => { window.location.href = url; });
              }}>
                Reconnect to Klaviyo
              </Button>
            </div>
          </div>
        ) : (
          <div className="settings-row">
            <div className="settings-field">
              <div className="settings-label">Authentication</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 13, color: "#059669", fontWeight: 500 }}>
                Connected via OAuth
              </span>
              <Button size="small" onClick={() => {
                getOAuthAuthorizeUrl().then(url => { window.location.href = url; });
              }}>
                Reconnect
              </Button>
            </div>
          </div>
        )}

        <div className="settings-row">
          <div className="settings-field">
            <div className="settings-label">Status</div>
          </div>
          <Switch
            checked={enabled}
            onChange={handleEnabledChange}
            checkedChildren="Enabled"
            unCheckedChildren="Disabled"
          />
        </div>
      </div>

      {/* Catalog Sync */}
      <div className="settings-section">
        <div className="settings-section-title">Catalog Sync</div>

        <div className="settings-row">
          <div className="settings-field">
            <div className="settings-label">Sync Schedule</div>
            {schedule === "manual_only" && (
              <div className="settings-hint">Manual triggers only</div>
            )}
          </div>
          <Select
            value={schedule}
            onChange={handleScheduleChange}
            style={{ width: 220 }}
            options={SCHEDULE_OPTIONS}
          />
        </div>
      </div>

      {/* Webhook Setup */}
      <div className="settings-section">
        {formatWebhookDate() && (
          <div className="settings-hint" style={{ margin: "0 0 8px 4px" }}>
            {formatWebhookDate()}
          </div>
        )}
        <Spin spinning={rotatingSecret}>
          <WebhookSetupCard
            webhookUrl={summary?.webhook_url}
            onRotate={handleWebhookSecret}
          />
        </Spin>
      </div>

      {/* Danger Zone */}
      <div className="danger-zone">
        <div className="settings-section-title">Danger Zone</div>
        <div className="danger-description">
          Disconnect this Klaviyo integration. This will disable all event mappings and
          stop sending data to Klaviyo.
        </div>
        <Button className="danger-button" onClick={() => setDisconnectModalOpen(true)}>
          Disconnect
        </Button>
      </div>

      <DisconnectModal
        visible={disconnectModalOpen}
        onClose={() => setDisconnectModalOpen(false)}
        onDisconnect={onDisconnect}
      />

      <WebhookSecretModal
        visible={webhookSecretModalOpen}
        secret={webhookSecret}
        onDismiss={handleWebhookSecretDismiss}
      />
    </div>
  );
}
